import { useEffect } from 'react';
import { useQuery } from '@tanstack/react-query';
import { useAuthStore } from '../store/authstore';
import { useAcademicYearStore } from '../store/academicYearStore';
import { academicYearService } from '../services/api/academicYear';

/**
 * Hook to sync the academic year store with the backend.
 * Should be called once in a top-level component (e.g., DashboardLayout).
 *
 * This ensures:
 * 1. Default academic year is loaded when user logs in
 * 2. Store is cleared when user logs out
 * 3. Selected year is always valid
 *
 * Uses the same query keys as useAcademicYear so the cache/dedup is shared
 * with any other component reading academic years for this institute.
 */
export const useAcademicYearSync = () => {
  const { session, isAuthenticated } = useAuthStore();
  const {
    setDefaultAcademicYear,
    clearSelection,
  } = useAcademicYearStore();

  const instituteId = session?.user?.instituteId;

  const { data: activeYear } = useQuery({
    queryKey: ['academicYear', 'active', instituteId],
    queryFn: () => academicYearService.getActive(instituteId!),
    enabled: isAuthenticated && !!instituteId,
    staleTime: 5 * 60 * 1000,
  });

  // Only fetch the full list as a fallback when there's no active/default year.
  const { data: allYears } = useQuery({
    queryKey: ['academicYears', instituteId],
    queryFn: () => academicYearService.getAll(instituteId!),
    enabled: isAuthenticated && !!instituteId && activeYear === null,
    staleTime: 5 * 60 * 1000,
  });

  useEffect(() => {
    if (!isAuthenticated) {
      clearSelection();
      return;
    }

    if (!instituteId) return;

    if (activeYear) {
      setDefaultAcademicYear(activeYear.id, activeYear.name);
    } else if (allYears) {
      const validYear = allYears.find(y => y.status !== 'Archived');
      if (validYear) {
        setDefaultAcademicYear(validYear.id, validYear.name);
      }
    }
  }, [isAuthenticated, instituteId, activeYear, allYears, setDefaultAcademicYear, clearSelection]);
};
